import { FCElement } from '@fc/component';
import { QtyChangeEvent } from '@fc/events';

export function parseQuantityBound(value) {
  // eslint-disable-next-line eqeqeq
  if (value == null || value === '') return null;

  const parsed = Number.parseInt(String(value), 10);
  return Number.isFinite(parsed) ? parsed : null;
}

export function normalizeQuantityValue(input, value, fallbackValue = null) {
  if (!(input instanceof HTMLInputElement)) return null;

  const minValue = parseQuantityBound(input.min);
  const maxValue = parseQuantityBound(input.max);
  const stepValue = Math.max(parseQuantityBound(input.step) ?? 1, 1);

  let nextValue = Number.parseInt(String(value ?? ''), 10);
  if (!Number.isFinite(nextValue)) {
    nextValue = fallbackValue ?? minValue ?? 1;
  }

  if (minValue !== null && nextValue < minValue) {
    nextValue = minValue;
  }

  if (maxValue !== null && nextValue > maxValue) {
    nextValue = maxValue;
  }

  if (stepValue > 1) {
    const anchor = minValue ?? 0;
    nextValue = anchor + Math.round((nextValue - anchor) / stepValue) * stepValue;

    if (minValue !== null && nextValue < minValue) {
      nextValue = minValue;
    }

    if (maxValue !== null && nextValue > maxValue) {
      nextValue = maxValue;
    }
  }

  return nextValue;
}

export function syncQuantityInputValue(input, value, fallbackValue = null) {
  const nextValue = normalizeQuantityValue(input, value, fallbackValue);
  if (nextValue === null) return null;

  input.value = String(nextValue);
  input.setAttribute('value', String(nextValue));
  return nextValue;
}

export function resolveQuantityState(input, fallbackState = 'ready') {
  if (!(input instanceof HTMLInputElement)) return fallbackState;
  return input.disabled ? 'disabled' : 'ready';
}

function parseCartQuantity(value) {
  const parsed = Number.parseInt(String(value ?? ''), 10);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
}

class QuantitySelectorComponent extends FCElement {
  requiredRefs = ['quantityInput'];
  #abortController = null;

  connectedCallback() {
    super.connectedCallback();

    const input = this.#getInputMaybe();
    if (!(input instanceof HTMLInputElement)) return;

    this.#abortController = new AbortController();
    input.addEventListener('focus', this.#handleFocus, {
      signal: this.#abortController.signal,
      passive: true,
    });

    this.resetQuantityState();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    this.#abortController = null;
  }

  get quantityInput() {
    const input = this.refs.quantityInput;
    if (!input) {
      throw new Error('Missing <input ref="quantityInput" /> inside <qty-selector />');
    }

    return input;
  }

  selectInputValue(event) {
    this.#handleFocus(event);
  }

  increaseQuantity(event) {
    event?.preventDefault();
    if (this.#isInteractionLocked()) return;
    this.#stepInput(1);
  }

  decreaseQuantity(event) {
    event?.preventDefault();
    if (this.#isInteractionLocked()) return;
    this.#stepInput(-1);
  }

  setQuantity(event) {
    event?.preventDefault();
    if (this.#isInteractionLocked()) return;

    const input = this.#getInputMaybe();
    if (!(input instanceof HTMLInputElement)) return;

    const previousQuantity = normalizeQuantityValue(input, input.defaultValue, null);
    const nextQuantity = this.#syncInputFromEvent(event);
    if (nextQuantity === null) return;

    if (previousQuantity !== null && nextQuantity === previousQuantity) return;

    this.#emitChange();
  }

  handleButtonKeydown(event) {
    if (!(event?.target instanceof HTMLButtonElement)) return;
    if (event.target.disabled || this.#isInteractionLocked()) return;
    if (event.key !== 'Enter' && event.key !== ' ') return;

    event.preventDefault();

    if (event.target.name === 'plus') {
      this.#stepInput(1);
      return;
    }

    if (event.target.name === 'minus') {
      this.#stepInput(-1);
    }
  }

  resetQuantityState() {
    this.dataset.quantityState = resolveQuantityState(this.#getInputMaybe(), this.dataset.quantityState || 'ready');
  }

  setQuantityState(state) {
    if (typeof state !== 'string' || state.trim() === '') return;
    this.dataset.quantityState = state;
  }

  getValue() {
    return this.#getInputMaybe()?.value ?? '';
  }

  setValue(value) {
    const input = this.#getInputMaybe();
    if (!(input instanceof HTMLInputElement)) return null;
    return syncQuantityInputValue(input, value);
  }

  setCartQuantity(quantity) {
    const input = this.#getInputMaybe();
    if (!(input instanceof HTMLInputElement)) return;
    input.dataset.cartQuantity = String(parseCartQuantity(quantity));
  }

  getCurrentValues() {
    const input = this.#getInputMaybe();
    if (!(input instanceof HTMLInputElement)) {
      return {
        min: 1,
        max: null,
        step: 1,
        value: 0,
        cartQuantity: 0,
        isCartLine: false,
      };
    }

    return {
      min: parseQuantityBound(input.min) ?? 1,
      max: parseQuantityBound(input.max),
      step: Math.max(parseQuantityBound(input.step) ?? 1, 1),
      value: parseQuantityBound(input.value) ?? 0,
      cartQuantity: parseCartQuantity(input.dataset.cartQuantity),
      isCartLine: this.#isCartLineInput(input),
    };
  }

  getRemainingAddableQuantity() {
    const { max, cartQuantity, isCartLine } = this.getCurrentValues();
    if (isCartLine || max === null) return null;
    return Math.max(max - cartQuantity, 0);
  }

  canAddToCart() {
    const { max, cartQuantity, value, isCartLine } = this.getCurrentValues();
    const quantityToAdd = Math.max(value, 0);
    const remainingQuantity = this.getRemainingAddableQuantity();
    const wouldExceedMax = !isCartLine && max !== null && cartQuantity + quantityToAdd > max;

    return {
      canAdd: !wouldExceedMax,
      maxQuantity: max,
      cartQuantity,
      quantityToAdd,
      remainingQuantity,
    };
  }

  #handleFocus = (event) => {
    const input = this.#getInputMaybe();
    if (!(input instanceof HTMLInputElement)) return;
    if (this.#isInteractionLocked()) return;
    if (!(event.target instanceof HTMLInputElement)) return;
    if (document.activeElement !== input) return;

    requestAnimationFrame(() => input.select());
  };

  #getInputMaybe() {
    return this.refs.quantityInput;
  }

  #stepInput(direction) {
    const input = this.#getInputMaybe();
    if (!(input instanceof HTMLInputElement)) return;
    if (this.#isInteractionLocked()) return;

    if (direction > 0) {
      input.stepUp();
    } else {
      input.stepDown();
    }

    this.setQuantityState('ready');
    this.#emitChange();
  }

  #syncInputFromEvent(event) {
    if (!(event?.target instanceof HTMLInputElement)) return null;
    if (this.#isInteractionLocked()) return null;

    const input = this.#getInputMaybe();
    if (!(input instanceof HTMLInputElement)) return null;
    if (this.#isInteractionLocked()) return null;

    const nextQuantity = syncQuantityInputValue(input, event.target.value);
    this.setQuantityState('ready');
    return nextQuantity;
  }

  #emitChange() {
    const input = this.#getInputMaybe();
    if (!(input instanceof HTMLInputElement)) return;
    if (this.#isInteractionLocked()) return;

    this.#clampInput(input);

    const quantity = Number.parseInt(input.value, 10);
    if (!Number.isFinite(quantity) || quantity < 0) return;

    const lineId = this.#parseLineId(input.dataset.cartLine);
    const lineKey = input.dataset.cartKey || null;
    input.dispatchEvent(new QtyChangeEvent(quantity, lineId, lineKey));
  }

  #clampInput(input) {
    syncQuantityInputValue(input, input.value);
  }

  #parseLineId(value) {
    // eslint-disable-next-line eqeqeq
    if (value == null || value === '') return undefined;

    const parsed = Number(value);
    if (Number.isNaN(parsed)) return undefined;
    return parsed;
  }

  #isCartLineInput(input) {
    return Boolean(input.dataset.cartLine || input.dataset.cartKey || input.name === 'updates[]');
  }

  #isInteractionLocked() {
    const input = this.#getInputMaybe();
    return this.getAttribute('aria-disabled') === 'true' || this.dataset.quantityState === 'busy' || input?.disabled === true;
  }
}

if (!customElements.get('qty-selector')) {
  customElements.define('qty-selector', QuantitySelectorComponent);
}
